/**
 * O que, num cliente, exige acao da atendente — e em que ordem.
 *
 * A regra de negocio mora aqui e nao dentro do JSX porque ela e usada em tres lugares
 * (painel, coluna "Conexao" da tabela de clientes e pagina do cliente) e porque errar a
 * ordem custa dinheiro: um canal desconectado so aparece como reclamacao do medico dois
 * dias depois.
 */
import type { Channel, Client } from '@zenda/api-contract';
import type { Dictionary } from '../content/pt-BR';

export type AlertLevel = 'critical' | 'warn';

export interface ClientAlert {
  /** Peso na ordenacao. Quanto maior, mais cedo o cliente aparece na lista. */
  readonly weight: number;
  readonly level: AlertLevel;
  readonly title: string;
  /** O mesmo problema numa etiqueta de tabela. Sem ele a coluna estoura a linha. */
  readonly short: string;
  readonly text: string;
}

/** Dias sem eco do app do medico a partir dos quais a coexistencia esta morrendo. */
const ECHO_CRITICAL_DAYS = 7;

export function channelAlerts(t: Dictionary, channel: Channel): readonly ClientAlert[] {
  const alerts: ClientAlert[] = [];

  if (channel.state === 'disconnected') {
    alerts.push({
      weight: 100,
      level: 'critical',
      title: t.alerts.disconnectedTitle,
      short: t.alerts.disconnectedTitle,
      text: t.alerts.disconnectedText,
    });
  } else if (channel.state === 'suspended') {
    alerts.push({
      weight: 100,
      level: 'critical',
      title: t.alerts.suspendedTitle,
      short: t.alerts.suspendedTitle,
      text: t.alerts.suspendedText,
    });
  }

  // O caso silencioso: o numero conecta, a interface parece boa, e o envio morre.
  if (channel.billingStatus === 'missing_payment_method') {
    alerts.push({
      weight: 90,
      level: 'critical',
      title: t.alerts.noCardTitle,
      short: t.alerts.noCardShort,
      // Nao manda "peca para anexar o cartao": na pagina do cliente o texto pronto e o
      // link da Meta ficam logo abaixo deste alerta. Mandar procurar o que ja esta na
      // tela e o tipo de instrucao que faz a pessoa sair da tela para nada.
      text: t.alerts.noCardText,
    });
  } else if (channel.billingStatus === 'blocked') {
    alerts.push({
      weight: 90,
      level: 'critical',
      title: t.alerts.billingBlockedTitle,
      short: t.alerts.billingBlockedTitle,
      text: t.alerts.billingBlockedText,
    });
  } else if (channel.billingStatus === 'unknown') {
    alerts.push({
      weight: 20,
      level: 'warn',
      title: t.alerts.billingUnknownTitle,
      short: t.alerts.billingUnknownShort,
      text: t.alerts.billingUnknownText,
    });
  }

  if (channel.state === 'inactive_warning') {
    const days = channel.daysSinceLastEcho;
    const critical = days !== null && days >= ECHO_CRITICAL_DAYS;
    alerts.push({
      weight: critical ? 70 : 45,
      level: critical ? 'critical' : 'warn',
      title: t.alerts.inactiveTitle,
      short: t.alerts.inactiveTitle,
      text: days === null ? t.alerts.inactiveTextNoDays : t.alerts.inactiveText(days),
    });
  } else if (channel.state === 'degraded') {
    alerts.push({
      weight: 40,
      level: 'warn',
      title: t.alerts.degradedTitle,
      short: t.alerts.degradedTitle,
      text: t.alerts.degradedText,
    });
  }

  if (channel.qualityRating === 'RED') {
    alerts.push({
      weight: 65,
      level: 'critical',
      title: t.alerts.qualityRedTitle,
      short: t.alerts.qualityRedTitle,
      text: t.alerts.qualityRedText,
    });
  } else if (channel.qualityRating === 'YELLOW') {
    alerts.push({
      weight: 30,
      level: 'warn',
      title: t.alerts.qualityYellowTitle,
      short: t.alerts.qualityYellowTitle,
      text: t.alerts.qualityYellowText,
    });
  }

  return alerts;
}

/**
 * So o que diz respeito a CONEXAO. Fica separado do resto porque na tabela de clientes
 * a conexao tem coluna propria: misturar a mensalidade atrasada ali faria a coluna
 * "Conexao" mentir sobre um canal que esta perfeitamente no ar.
 */
function connectionAlerts(t: Dictionary, client: Client): readonly ClientAlert[] {
  const alerts: ClientAlert[] = [];

  if (client.channels.length === 0) {
    alerts.push({
      weight: 50,
      level: 'warn',
      title: t.alerts.noWhatsappTitle,
      short: t.alerts.noWhatsappShort,
      text: t.alerts.noWhatsappText,
    });
  }

  for (const channel of client.channels) alerts.push(...channelAlerts(t, channel));
  return alerts;
}

/** O pior problema de conexao, para resumir a coluna numa etiqueta so. */
export function worstConnectionAlert(t: Dictionary, client: Client): ClientAlert | undefined {
  return [...connectionAlerts(t, client)].sort((a, b) => b.weight - a.weight)[0];
}

function clientAlerts(t: Dictionary, client: Client): readonly ClientAlert[] {
  const alerts: ClientAlert[] = [];

  if (client.paymentOverdue) {
    alerts.push({
      weight: 80,
      level: 'critical',
      title: t.alerts.overdueTitle,
      short: t.alerts.overdueTitle,
      text: t.alerts.overdueText,
    });
  }

  alerts.push(...connectionAlerts(t, client));
  return alerts;
}

function attentionScore(t: Dictionary, client: Client): number {
  return clientAlerts(t, client).reduce((sum, a) => sum + a.weight, 0);
}

/**
 * Mesma definicao que o `clients_attention` do `/api/dashboard/stats`: cliente ativo com
 * canal caido, sem cobranca em dia, inativo/degradado ou com qualidade em queda.
 *
 * Ela e repetida aqui de proposito e precisa continuar identica — um numero no tile que
 * nao bate com a lista logo abaixo destroi a confianca nos dois.
 */
export function needsAttention(client: Client): boolean {
  return (
    client.status === 'active' &&
    client.channels.some(
      (ch) =>
        ch.state === 'disconnected' ||
        ch.state === 'suspended' ||
        ch.state === 'inactive_warning' ||
        ch.state === 'degraded' ||
        ch.billingStatus !== 'ok' ||
        ch.qualityRating === 'YELLOW' ||
        ch.qualityRating === 'RED',
    )
  );
}

/** O motivo mais grave, para resumir o cliente numa linha so. */
export function worstAlert(t: Dictionary, client: Client): ClientAlert | undefined {
  return [...clientAlerts(t, client)].sort((a, b) => b.weight - a.weight)[0];
}

/**
 * Quem precisa de atencao primeiro; empate resolve pelo nome, para a ordem nao dancar.
 *
 * Recebe o dicionario porque `attentionScore` monta os alertas para soma-los, e
 * `localeCompare` recebe a tag para ordenar acentos como cada lingua espera —
 * em pt-BR "Alvaro" vem antes de "Alzira", e a ordem alfabetica de uma lista de
 * clientes e algo que a pessoa percebe quando esta errada.
 */
export function byAttention(t: Dictionary, locale: string): (a: Client, b: Client) => number {
  return (a, b) => {
    const diff = attentionScore(t, b) - attentionScore(t, a);
    return diff !== 0 ? diff : a.name.localeCompare(b.name, locale);
  };
}
